import { LucideIcon, BarChart, Cpu, Settings } from 'lucide-react';

interface Framework {
  number: string;
  title: string;
  description: string;
  icon: LucideIcon;
  dimensions: string[];
  numGradient: string;
  iconBg: string;
  iconBorder: string;
  iconColor: string;
}

const frameworks: Framework[] = [
  {
    number: '01',
    title: "Technology Modernization Maturity Model",
    description: "Assess your organization across 6 dimensions: Infrastructure, Applications, Data, Security, Team Capability, and Governance. Identify gaps and prioritize investments.",
    icon: BarChart,
    dimensions: ['Infrastructure', 'Applications', 'Data', 'Security', 'Team Capability', 'Governance'],
    numGradient: 'from-em-deeper to-em',
    iconBg: 'bg-em-light',
    iconBorder: 'border-em-border',
    iconColor: 'text-em-deeper',
  },
  {
    number: '02',
    title: "MCP & Agent-First AI Development",
    description: "Build AI that works in production. Our 4-phase framework takes you from use-case selection to a live MCP-integrated agent — with API design, LLM orchestration, and exec reporting built in.",
    icon: Cpu,
    dimensions: ['Use case & API scoping', 'MCP server design', 'Agent orchestration', 'Prod ship & reporting'],
    numGradient: 'from-em to-teal',
    iconBg: 'bg-teal-light',
    iconBorder: 'border-teal-border',
    iconColor: 'text-teal-deeper',
  },
  {
    number: '03',
    title: "Operational Efficiency & Execution Maturity",
    description: "Diagnose operational bottlenecks and improve execution velocity. Assess process maturity, tooling effectiveness, and team alignment to accelerate business outcomes.",
    icon: Settings,
    dimensions: ['Process maturity', 'Tooling effectiveness', 'Team alignment', 'Execution velocity'],
    numGradient: 'from-teal to-teal-dark',
    iconBg: 'bg-teal-light',
    iconBorder: 'border-teal-border',
    iconColor: 'text-teal-deeper',
  },
];

export const Frameworks = () => {
  return (
    <section id="frameworks" className="bg-white py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center space-y-4 mb-16">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-teal-light border border-teal-border text-xs text-teal-deeper font-semibold">
            Proven Frameworks
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Clarity without the guesswork.</h2>
          <p className="text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Structured, repeatable methodologies that provide executive-ready decision points and measurable outcomes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {frameworks.map((framework) => (
            <div
              key={framework.number}
              className="group relative bg-white rounded-3xl border border-slate-100 shadow-card hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300 overflow-hidden"
            >
              <div className="absolute inset-x-0 top-0 h-0.5 gradient-em opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="p-8">
                <p className={`text-[4.5rem] leading-none font-bold mb-4 -ml-1 select-none bg-gradient-to-br ${framework.numGradient} bg-clip-text text-transparent opacity-[0.12] group-hover:opacity-[0.22] transition-opacity duration-300`}>
                  {framework.number}
                </p>

                <div className={`h-10 w-10 rounded-xl ${framework.iconBg} border ${framework.iconBorder} flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110`}>
                  <framework.icon className={`h-5 w-5 ${framework.iconColor}`} />
                </div>

                <h3 className="text-sm font-bold text-slate-900 mb-3 leading-snug">{framework.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-6">{framework.description}</p>

                <div className="flex flex-wrap gap-1.5">
                  {framework.dimensions.map((d) => (
                    <span
                      key={d}
                      className="text-xs px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 border border-slate-100 group-hover:bg-em-light group-hover:border-em-border group-hover:text-em-deeper transition-colors duration-300"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
